import { useCallback, useState, type ReactNode } from 'react';
import { errorMessage } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';

export interface ConfirmDialogProps {
  title: string;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  /** What will happen, stated plainly. Every destructive action needs one. */
  children: ReactNode;
  confirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}

/**
 * A confirmation step that keeps the server's own wording.
 *
 * A rejected action (in-flight claims, a reporting loop, the Finance Approver
 * cap) is reported by the server in words the admin can act on, so the failure
 * is shown here rather than swallowed into a generic "could not save".
 */
export function ConfirmDialog({
  title,
  confirmLabel,
  tone = 'primary',
  children,
  confirmDisabled = false,
  onCancel,
  onConfirm,
}: ConfirmDialogProps): ReactNode {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }, [onConfirm]);

  return (
    <Modal title={title} onClose={busy ? () => undefined : onCancel}>
      <div style={{ fontSize: 12.5 }}>{children}</div>
      {error ? <div className="error-text">{error}</div> : null}
      <div className="cf-submitbar" style={{ marginTop: 14 }}>
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={tone === 'danger' ? 'btn btn-danger' : 'btn btn-primary'}
          onClick={() => void run()}
          disabled={busy || confirmDisabled}
        >
          {busy ? <Spinner /> : null}
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
